import React from "react";
import { Link } from "react-router-dom";
import { Clock3, Award, CheckCircle2, ArrowRight, IdCard, Wallet, AlarmClock, FileText, Phone, ListChecks, Monitor, CalendarClock, FileCheck2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { TESTS, ADDITIONAL_TESTS, OTHER_SERVICES, ONLINE_PAYMENTS_ENABLED, IMAGES, SITE } from "../mock";

const checklist = [
  { icon: IdCard, title: "2 Forms of ID", desc: "Each testee must provide 2 forms of government identification — one being their passport." },
  { icon: AlarmClock, title: "Arrive 20 Minutes Early", desc: "Late arrivals will not be allowed to take the test. Please plan accordingly." },
  { icon: Wallet, title: "Payment at Check In", desc: "Payment must be made on or before check in — cash, debit/credit card or cheque." },
  { icon: FileText, title: "Work Permit Copy", desc: "If testing for a work permit application, bring a copy of your work permit to check in." },
];

const testImages = { "bridge-lr": IMAGES.examRoom, "toeic-lr": IMAGES.examWriting, "toeic-sw": IMAGES.consultation, "toeic-lr-public": IMAGES.workers };

export default function Services() {
  return (
    <div data-testid="services-page">
      <div className="bg-[#0A2540] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-[#39C1D0] font-bold text-sm uppercase tracking-widest mb-3">Testing Services</div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">Official TOEIC® Testing in the Cayman Islands</h1>
          <p className="mt-5 text-slate-300 max-w-2xl mx-auto leading-relaxed">
            As the authorized ETS testing center for the Cayman Islands, we administer the full family of TOEIC® tests at our George Town facility — trusted by employers, immigration and professionals across the islands.
          </p>
        </div>
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-16">
          {TESTS.map((t, idx) => (
            <div key={t.id} className={`grid lg:grid-cols-2 gap-12 items-center ${idx % 2 === 1 ? "lg:[direction:rtl]" : ""}`}>
              <div className="lg:[direction:ltr]">
                <img src={testImages[t.id]} alt={t.shortName} className="rounded-2xl shadow-xl w-full h-72 object-cover" />
              </div>
              <div className="lg:[direction:ltr]">
                <Badge className="bg-[#E8F7F9] text-[#0C8695] hover:bg-[#E8F7F9] border border-[#B5E6EC] font-semibold mb-4">{t.badge}</Badge>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540] tracking-tight leading-snug">{t.name}</h2>
                <p className="mt-4 text-slate-600 leading-relaxed">{t.description}</p>
                <ul className="mt-6 space-y-3 text-sm text-slate-600">
                  <li className="flex items-center gap-2.5"><Clock3 size={16} className="text-[#0E9AAA]" /> <strong className="text-[#0A2540]">Schedule:</strong> {t.schedule}</li>
                  <li className="flex items-center gap-2.5"><AlarmClock size={16} className="text-[#0E9AAA]" /> <strong className="text-[#0A2540]">Duration:</strong> {t.duration}</li>
                  <li className="flex items-center gap-2.5"><ListChecks size={16} className="text-[#0E9AAA]" /> <strong className="text-[#0A2540]">Questions:</strong> {t.questions}</li>
                  <li className="flex items-center gap-2.5"><Award size={16} className="text-[#0E9AAA]" /> <strong className="text-[#0A2540]">Scoring:</strong> {t.scoreScale}</li>
                  <li className="flex items-center gap-2.5"><CheckCircle2 size={16} className="text-[#0E9AAA]" /> <strong className="text-[#0A2540]">Results:</strong> {t.results}</li>
                  <li className="flex items-center gap-2.5"><Wallet size={16} className="text-[#0E9AAA]" /> <strong className="text-[#0A2540]">Cost:</strong> {t.priceNote}</li>
                </ul>
                <Link to={`/book?test=${t.id}`} className="inline-block mt-7">
                  <Button className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-7 shadow-md">
                    Book This Test <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INDIVIDUAL SKILL TESTS */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-[#0E9AAA] font-bold text-sm uppercase tracking-widest mb-3">Individual Skill Tests</div>
            <h2 className="text-3xl font-extrabold text-[#0A2540] tracking-tight">Single-Section TOEIC® Tests</h2>
            <p className="mt-4 text-slate-600">
              Need to certify just one skill? Both single-section tests run on Fridays at 12:30 PM with only 2 seats per session — book online early to secure your spot.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-7 max-w-4xl mx-auto">
            {ADDITIONAL_TESTS.map((t) => (
              <Card
                key={t.id}
                data-testid={`skill-test-card-${t.id}`}
                className="rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <CardContent className="p-8 flex flex-col flex-1">
                  <Badge className="self-start bg-[#E8F7F9] text-[#0C8695] hover:bg-[#E8F7F9] border border-[#B5E6EC] font-semibold mb-4">
                    {t.badge}
                  </Badge>
                  <h3 className="font-extrabold text-xl text-[#0A2540]">{t.name}</h3>
                  <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{t.description}</p>
                  <ul className="mt-5 space-y-2.5 text-sm text-slate-600 flex-1">
                    <li className="flex items-center gap-2.5"><ListChecks size={15} className="text-[#0E9AAA]" /> {t.questions}</li>
                    <li className="flex items-center gap-2.5"><Clock3 size={15} className="text-[#0E9AAA]" /> {t.duration}</li>
                    <li className="flex items-center gap-2.5"><Award size={15} className="text-[#0E9AAA]" /> {t.level}</li>
                    <li className="flex items-center gap-2.5"><CalendarClock size={15} className="text-[#0E9AAA]" /> {t.availability}</li>
                    <li className="flex items-center gap-2.5"><Monitor size={15} className="text-[#0E9AAA]" /> {t.mode}</li>
                  </ul>
                  <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                    <span className="font-extrabold text-[#0A2540] text-2xl">CI ${t.price}.00</span>
                    <Link to={`/book?test=${t.id}`}>
                      <Button size="sm" className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg transition-colors">
                        Book This Test <ArrowRight size={14} className="ml-1.5" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ADDITIONAL SERVICES */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-[#0E9AAA] font-bold text-sm uppercase tracking-widest mb-3">Additional Services</div>
            <h2 className="text-3xl font-extrabold text-[#0A2540] tracking-tight">Certified Copies of Test Results</h2>
          </div>
          {OTHER_SERVICES.map((s) => (
            <Card key={s.id} data-testid={`services-service-card-${s.id}`} className="rounded-2xl border-slate-100 shadow-sm max-w-4xl mx-auto">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="w-14 h-14 rounded-xl bg-[#E8F7F9] border border-[#B5E6EC] flex items-center justify-center shrink-0">
                    <FileCheck2 className="text-[#0E9AAA]" size={26} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-extrabold text-[#0A2540] text-lg">{s.name}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.description}</p>
                    <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
                      <Phone size={13} className="text-[#0E9AAA]" /> {s.availability} {SITE.phone} • {SITE.email}
                    </p>
                  </div>
                  <div className="md:text-right shrink-0">
                    <div className="font-extrabold text-[#0A2540] text-2xl">CI ${Number(s.price).toFixed(2)}</div>
                    <div className="text-xs text-slate-400 font-semibold mt-1">per copy</div>
                    {ONLINE_PAYMENTS_ENABLED && (
                      <Link to="/certified-copy">
                        <Button data-testid="services-request-copy-btn" className="mt-3 bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg">
                          Request & Pay Online <ArrowRight size={15} className="ml-1.5" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="py-20 bg-[#F6FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl font-extrabold text-[#0A2540] tracking-tight">What to Bring on Test Day</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {checklist.map((c) => (
              <div key={c.title} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-[#E8F7F9] flex items-center justify-center mb-4">
                  <c.icon className="text-[#0E9AAA]" size={22} />
                </div>
                <h3 className="font-bold text-[#0A2540]">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 bg-[#0A2540] rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-white font-extrabold text-xl">Testing in the Sister Islands</h3>
              <p className="text-slate-300 text-sm mt-2">To schedule a test in Cayman Brac or Little Cayman, please email {SITE.sisterIslandsEmail}.</p>
            </div>
            <a href={`mailto:${SITE.sisterIslandsEmail}`}>
              <Button className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-7 whitespace-nowrap">Email Us</Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
